import React, { useRef, useEffect } from "react";
import { ILandingFooterProps } from "../../LandingFooter";
import {
  COMBINED_SOCIAL_PATH,
  COMBINED_CLICK_ZONES,
  PINTEREST_PATH,
} from "./GradientSocialIcons.icons";

// The combined FB+IG+TT path occupies a 71×16 space (see GradientSocialIcons.icons.tsx)
const COMBINED_BASE_W = 71;
const COMBINED_BASE_H = 16;
const PINTEREST_BASE = 24;
const ROW_BASE_H = 24;
const GAP_BASE = 16;

/** Uniform scale vs original 24px-tall row (icons + gaps) */
const ICON_SCALE = 1.2;

const SVG_H = ROW_BASE_H * ICON_SCALE;
const COMBINED_W = COMBINED_BASE_W * ICON_SCALE;
const PINTEREST_SIZE = PINTEREST_BASE * ICON_SCALE;
const GAP = GAP_BASE * ICON_SCALE;
const COMBINED_Y = (SVG_H - COMBINED_BASE_H * ICON_SCALE) / 2;

const GRAD_ID = "ac-footer-social-shared";

const COMBINED_KEYS = ["facebook", "instagram", "tiktok"] as const;

interface GradientSocialIconsProps {
  socialLinks: NonNullable<ILandingFooterProps["socialLinks"]>;
}

export const GradientSocialIcons = ({
  socialLinks,
}: GradientSocialIconsProps) => {
  const hasCombined = COMBINED_KEYS.some((k) => !!socialLinks[k]);
  const hasPinterest = !!socialLinks.pinterest;

  if (!hasCombined && !hasPinterest) return null;

  const pinterestX = hasCombined ? COMBINED_W + GAP : 0;
  const svgWidth =
    (hasCombined ? COMBINED_W : 0) +
    (hasCombined && hasPinterest ? GAP : 0) +
    (hasPinterest ? PINTEREST_SIZE : 0);

  const gradRef = useRef<SVGLinearGradientElement>(null);

  useEffect(() => {
    const grad = gradRef.current;
    if (!grad) return;

    const DURATION = 3000; // ms
    const startTime = performance.now();
    let frame: number;

    const animate = (now: number) => {
      const progress = ((now - startTime) % DURATION) / DURATION;
      grad.setAttribute("gradientTransform", `translate(${-progress * svgWidth}, 0)`);
      frame = requestAnimationFrame(animate);
    };

    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [svgWidth]);

  return (
    <svg
      width={svgWidth}
      height={SVG_H}
      viewBox={`0 0 ${svgWidth} ${SVG_H}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient
          ref={gradRef}
          id={GRAD_ID}
          x1="0"
          y1="0"
          x2={svgWidth}
          y2="0"
          gradientUnits="userSpaceOnUse"
          spreadMethod="repeat"
        >
          <stop stopColor="#00A8FF" />
          <stop offset="0.25" stopColor="#2900FF" />
          <stop offset="0.5" stopColor="#1ABEFF" />
          <stop offset="0.75" stopColor="#00DAB5" />
          <stop offset="1" stopColor="#00A8FF" />
        </linearGradient>
      </defs>

      {hasCombined && (
        <>
          <g transform={`translate(0, ${COMBINED_Y}) scale(${ICON_SCALE})`}>
            <path
              d={COMBINED_SOCIAL_PATH}
              fill={`url(#${GRAD_ID})`}
              fillRule="evenodd"
            />
          </g>
          {COMBINED_KEYS.map((key) => {
            const url = socialLinks[key];
            if (!url) return null;
            const zone = COMBINED_CLICK_ZONES[key];
            return (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noreferrer"
                aria-label={key}
              >
                <rect
                  x={zone.x * ICON_SCALE}
                  y={0}
                  width={zone.width * ICON_SCALE}
                  height={SVG_H}
                  fill="transparent"
                />
              </a>
            );
          })}
        </>
      )}

      {hasPinterest && (
        <a
          href={socialLinks.pinterest}
          target="_blank"
          rel="noreferrer"
          aria-label="pinterest"
        >
          <g transform={`translate(${pinterestX}, 0)`}>
            <g transform={`scale(${ICON_SCALE})`}>
              <path
                d={PINTEREST_PATH}
                fill={`url(#${GRAD_ID})`}
                fillRule="evenodd"
              />
            </g>
          </g>
        </a>
      )}
    </svg>
  );
};

export default GradientSocialIcons;
